import axios from 'axios';

import { COREAPI, getUserData } from '../constants/global';
import type { AgentAssignmentOutcome, IAgentByEntityResponse } from '../interface/agent-assignment';

// Standalone client for the Core API's agent-coverage endpoint. It attaches the
// agent's existing core JWT + PartyId (the same headers the Redux thunk sends),
// but has NO 401->logout interceptor: this is auxiliary view data, so a failure
// here must never sign the agent out or fire the global error toast. We also need
// the repeatable `EntityCode` array param, which the shared query helper can't
// emit — URLSearchParams.append handles it.
const agentAssignmentApi = axios.create({ baseURL: COREAPI });

agentAssignmentApi.interceptors.request.use((config) => {
	const { token, user } = getUserData();
	if (token) config.headers.Authorization = `Bearer ${token}`;
	if (user?.PartyId) config.headers.PartyId = user.PartyId;
	return config;
});

// The core service sometimes returns JSON with a Content-Type that prevents axios
// from auto-parsing — coerce string bodies back to JSON.
agentAssignmentApi.interceptors.response.use((response) => {
	if (typeof response.data === 'string' && response.data) {
		try {
			response.data = JSON.parse(response.data);
		} catch {
			// Not JSON — leave as-is.
		}
	}
	return response;
});

const MAX_CODES_PER_REQUEST = 200;

/**
 * Fetch the agent(s) assigned to the given child entity codes.
 *
 * `EntityCode` is repeated once per code. `ResultType` is intentionally omitted —
 * the backend infers the level from each code's dash-segment count (safer than
 * guessing). `ElectionId` is required server-side. Non-2xx responses resolve
 * (validateStatus) so the caller can read the body and classify (e.g. 403).
 */
export const getAssignmentsByEntity = async (codes: string[], electionId: number): Promise<AgentAssignmentOutcome> => {
	const params = new URLSearchParams();
	codes.slice(0, MAX_CODES_PER_REQUEST).forEach((code) => params.append('EntityCode', code));
	params.append('ElectionId', String(electionId));
	params.append('ResultSource', 'Agent');

	try {
		const res = await agentAssignmentApi.get<IAgentByEntityResponse>('/api/v1/AgentAssignment/by-entity', {
			params,
			validateStatus: () => true,
		});
		return { reachable: true, httpStatus: res.status, body: res.data };
	} catch {
		return { reachable: false };
	}
};
